import { queryClient } from '@/config/query-client.config';
import { GlowUpProduct } from '@repo/types/src/glow-up-product';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  activateGlowUpProduct,
  createGlowUpProduct,
  deactivateGlowUpProduct,
  deleteGlowUpProduct,
  getAllGlowUpProducts,
  reorderGlowUpProducts,
  updateGlowUpProduct,
} from './glow-up-product.queries';
import { glowUpProductQueryKeys } from './glow-up-product.query-keys';

export const useGetAllGlowUpProducts = () => {
  const {
    data: products,
    isError,
    isLoading,
  } = useQuery({
    queryKey: glowUpProductQueryKeys.all,
    queryFn: getAllGlowUpProducts,
  });
  return { products: products ?? [], isError, isLoading };
};

export const useCreateGlowUpProduct = () =>
  useMutation({
    mutationFn: (payload: {
      categoryId: number;
      title: string;
      description?: string;
      link: string;
      ctaText: string;
      position?: number;
      active?: boolean;
      file: File;
    }) => createGlowUpProduct(payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: glowUpProductQueryKeys.byCategory(variables.categoryId),
      });
    },
  });

export const useUpdateGlowUpProduct = () =>
  useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number;
      payload: {
        categoryId: number;
        title: string;
        description?: string;
        link: string;
        ctaText: string;
        position?: number;
        active?: boolean;
        file?: File;
      };
    }) => updateGlowUpProduct(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all });
      queryClient.invalidateQueries({
        queryKey: glowUpProductQueryKeys.byCategory(variables.payload.categoryId),
      });
    },
  });

export const useActivateGlowUpProduct = () =>
  useMutation({
    mutationFn: (id: number) => activateGlowUpProduct(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all }),
  });

export const useDeactivateGlowUpProduct = () =>
  useMutation({
    mutationFn: (id: number) => deactivateGlowUpProduct(id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all }),
  });

export const useDeleteGlowUpProduct = () =>
  useMutation({
    mutationFn: (id: number) => deleteGlowUpProduct(id),
    onSuccess: (data: GlowUpProduct) => {
      queryClient.setQueryData<GlowUpProduct[]>(
        glowUpProductQueryKeys.all,
        (old) => old?.filter((p) => p.id !== data.id),
      );
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all });
    },
  });

export const useReorderGlowUpProducts = () =>
  useMutation({
    mutationFn: (items: { id: number; position: number }[]) =>
      reorderGlowUpProducts(items),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: glowUpProductQueryKeys.all }),
  });
